import createProgram from './helpers/createProgram';
import createQuadVertexBuffer from './helpers/createQuadVertexBuffer';

const PENCIL_COLOR_INDEX = 7;

const HOW_MANY_REFLECTANCE_TEXTURES = 13;
const VECTOR_SIZE = 3;

class CurrentLayer {
  constructor(gl, shaders, textureManager, data) {
    this.gl = gl;
    this.shaders = shaders;
    this.textureManager = textureManager;
    this.data = data;

    this.dataMedium = this.data.medium.absorption.nominal;
    this.dataPencilImpasto = this.data.colors[PENCIL_COLOR_INDEX].absorption.impasto.map((k) => k * 0.5);
    this.dataPencilGlaze = this.data.colors[PENCIL_COLOR_INDEX].absorption.glaze.map((k) => k * 0.3);

    this.vectorImpasto = [0, 0, 0];
    this.vectorGlaze = [0, 0, 0];
    this.vectorMedium = [0, 0, 0];
    this.vectorPencilImpasto = [0, 0, 0];
    this.vectorPencilGlaze = [0, 0, 0];

    this.createBuffers();
    this.createPrograms();
  }

  createBuffers() {
    this.simulationFramebuffer = this.gl.createFramebuffer();
    this.quadVertexBuffer = createQuadVertexBuffer(this.gl);
  }

  createPrograms() {
    this.calculateColorProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.calculateColorFrag,
      ['a_position'],
      [
        'uAbsorptionGlaze_0',
        'uAbsorptionGlaze_1',
        'uAbsorptionGlaze_2',
        'uAbsorptionGlaze_3',
        'uAbsorptionGlaze_4',
        'uAbsorptionGlaze_5',
        'uAbsorptionGlaze_6',
        'uAbsorptionGlaze_7',
        'uAbsorptionGlaze_8',
        'uAbsorptionGlaze_9',
        'uAbsorptionGlaze_10',
        'uAbsorptionGlaze_11',
        'uAbsorptionGlaze_12',

        'uAbsorptionImpasto_0',
        'uAbsorptionImpasto_1',
        'uAbsorptionImpasto_2',
        'uAbsorptionImpasto_3',
        'uAbsorptionImpasto_4',
        'uAbsorptionImpasto_5',
        'uAbsorptionImpasto_6',
        'uAbsorptionImpasto_7',
        'uAbsorptionImpasto_8',
        'uAbsorptionImpasto_9',
        'uAbsorptionImpasto_10',
        'uAbsorptionImpasto_11',
        'uAbsorptionImpasto_12',

        'uBrushTexture',
        'uPencilTexture',
        'uSurfaceTexture',

        'uLimitGlaze',

        'uDontReadOlds',
      ]
    );

    this.surfaceProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.surfaceFrag,
      ['a_position'],
      ['u_brushTexture', 'u_paintingResolution', 'u_normalScale', 'uDontReadOlds']
    );

    this.brdfProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.brdfFrag,
      ['a_position'],
      [
        'uColorTexture',
        'uStructureTexture',
        'u_F0',
        'u_roughness',
        'u_diffuseScale',
        'u_specularScale',
        'u_lightDirection',
        'u_eyeDirection',
        'u_normalScale',
        'u_paintingResolution',
        'uDontReadOlds',
      ]
    );
  }

  update(state, props) {
    let paintIndex = state.paintIndex;

    this.dataGlaze = this.data.colors[paintIndex].absorption.glaze;
    this.dataImpasto = this.data.colors[paintIndex].absorption.impasto;
    this.modelLimit = this.data.colors[paintIndex].modelLimit;

    this.calculateVectors(state);
    this.calculateProperties(state);

    this.showPencil = props.showPencil;
  }

  calculateVectors(state) {
    let paintIndex = state.paintIndex;

    const glaze = this.data.colors[paintIndex].absorption.glaze;
    const impasto = this.data.colors[paintIndex].absorption.impasto;

    this.vectorsImpasto = [];
    this.vectorsGlaze = [];

    for (let i = 0; i < HOW_MANY_REFLECTANCE_TEXTURES; ++i) {
      const vectorImpasto = [];
      const vectorGlaze = [];

      for (let j = 0; j < VECTOR_SIZE; ++j) {
        const k = i * VECTOR_SIZE + j;

        vectorImpasto[j] = impasto[k];
        vectorGlaze[j] = glaze[k];
      }

      this.vectorsImpasto.push(vectorImpasto);
      this.vectorsGlaze.push(vectorGlaze);
    }
  }

  calculateProperties(state) {
    this.properties = {};

    this.properties.roughness = state.roughness;
    this.properties.F0 = state.F0;
    this.properties.diffuseScale = state.diffuseScale;
    this.properties.specularScale = state.specularScale;

    this.properties.lightDirection = [state.lightDirectionX, state.lightDirectionY, state.lightDirectionZ];

    this.properties.eyeDirection = [state.eyeDirectionX, state.eyeDirectionY, state.eyeDirectionZ];

    this.properties.normalScale = state.minimalNormalScale;
  }

  connect() {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);

    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.textureManager.currentLayerSurfaceTexture, 0);

    this.gl.enable(this.gl.DEPTH_TEST);
    this.gl.enable(this.gl.BLEND);
    this.gl.blendEquation(this.gl.FUNC_ADD);
    this.gl.blendFuncSeparate(this.gl.ONE, this.gl.ZERO, this.gl.ONE, this.gl.ZERO);

    this.gl.viewport(0, 0, this.textureManager.width, this.textureManager.height);

    this.gl.enable(this.gl.SCISSOR_TEST);
    this.gl.scissor(0, 0, this.textureManager.width, this.textureManager.height);

    this.gl.useProgram(this.surfaceProgram);

    this.gl.uniform2f(this.surfaceProgram.u_paintingResolution, this.textureManager.width, this.textureManager.height);
    this.gl.uniform1f(this.surfaceProgram.u_normalScale, this.properties.normalScale);
    this.gl.uniform1i(this.surfaceProgram.uDontReadOlds, true);

    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.splatBrushPaintTexture);
    this.gl.uniform1i(this.surfaceProgram.u_brushTexture, 0);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.surfaceProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.surfaceProgram.a_position);

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
  }

  render() {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);

    this.gl.enable(this.gl.DEPTH_TEST);
    this.gl.enable(this.gl.BLEND);
    this.gl.blendEquation(this.gl.FUNC_ADD);
    this.gl.blendFunc(this.gl.ONE, this.gl.ZERO);

    this.gl.viewport(0, 0, this.textureManager.width, this.textureManager.height);

    this.gl.enable(this.gl.SCISSOR_TEST);
    this.gl.scissor(0, 0, this.textureManager.width, this.textureManager.height);

    this.gl.useProgram(this.brdfProgram);

    this.gl.uniform2f(this.brdfProgram.u_paintingResolution, this.textureManager.width, this.textureManager.height);
    this.gl.uniform1f(this.brdfProgram.u_roughness, this.properties.roughness);
    this.gl.uniform1f(this.brdfProgram.u_F0, this.properties.F0);
    this.gl.uniform1f(this.brdfProgram.u_diffuseScale, this.properties.diffuseScale);
    this.gl.uniform1f(this.brdfProgram.u_specularScale, this.properties.specularScale);
    this.gl.uniform3fv(this.brdfProgram.u_lightDirection, this.properties.lightDirection);
    this.gl.uniform3fv(this.brdfProgram.u_eyeDirection, this.properties.eyeDirection);
    this.gl.uniform1f(this.brdfProgram.u_normalScale, this.properties.normalScale);

    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.currentLayerColorTexture);
    this.gl.uniform1i(this.brdfProgram.uColorTexture, 0);

    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.currentLayerSurfaceTexture);
    this.gl.uniform1i(this.brdfProgram.uStructureTexture, 1);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.brdfProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.brdfProgram.a_position);

    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.textureManager.currentLayerOutputTexture, 0);

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
  }

  calculateColor() {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);

    this.gl.disable(this.gl.DEPTH_TEST);
    this.gl.disable(this.gl.BLEND);

    this.gl.useProgram(this.calculateColorProgram);

    this.gl.viewport(0, 0, this.textureManager.width, this.textureManager.height);

    this.gl.enable(this.gl.SCISSOR_TEST);
    this.gl.scissor(0, 0, this.textureManager.width, this.textureManager.height);

    this.gl.uniform1f(this.calculateColorProgram.uLimitGlaze, this.modelLimit);
    this.gl.uniform1i(this.calculateColorProgram.uDontReadOlds, true);

    for (let i = 0; i < HOW_MANY_REFLECTANCE_TEXTURES; ++i) {
      this.gl.uniform3fv(this.calculateColorProgram[`uAbsorptionImpasto_${i}`], this.vectorsImpasto[i]);
      this.gl.uniform3fv(this.calculateColorProgram[`uAbsorptionGlaze_${i}`], this.vectorsGlaze[i]);
    }

    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.splatBrushPaintTexture);
    this.gl.uniform1i(this.calculateColorProgram.uBrushTexture, 0);

    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.surfaceTexture);
    this.gl.uniform1i(this.calculateColorProgram.uSurfaceTexture, 1);

    this.gl.activeTexture(this.gl.TEXTURE2);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.showPencil ? this.textureManager.splatPencilPaintTexture : undefined);
    this.gl.uniform1i(this.calculateColorProgram.uPencilTexture, 2);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.calculateColorProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.calculateColorProgram.a_position);

    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.textureManager.currentLayerColorTexture, 0);

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
  }
}

export default CurrentLayer;
